import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheck, FiCopy, FiRefreshCw } from "react-icons/fi";
import { CopyableCodeBlock } from "./dashboardSkillGuideCommon";

const LONG_PENDING_MS = 45000;

const sectionStyle = {
  marginTop: "28px",
};

const headerRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  marginBottom: "12px",
};

const titleStyle = {
  color: "#3730a3",
  fontSize: "18px",
  fontWeight: "700",
  margin: 0,
};

const metaStyle = {
  fontSize: "13px",
  color: "#64748b",
  marginBottom: "12px",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  wordBreak: "break-all",
};

const pollButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  height: "36px",
  padding: "0 14px",
  borderRadius: "6px",
  border: "1px solid #c7d2fe",
  backgroundColor: "#eef2ff",
  color: "#3730a3",
  fontWeight: 600,
  fontSize: "13px",
  cursor: "pointer",
};

const pollButtonDisabledStyle = {
  ...pollButtonStyle,
  opacity: 0.6,
  cursor: "not-allowed",
};

const statusBadgeStyle = (status) => {
  const normalized = String(status || "").toLowerCase();
  const colors =
    normalized === "completed"
      ? { bg: "#d1fae5", color: "#047857", border: "#6ee7b7" }
      : normalized === "failed"
      ? { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" }
      : { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" };

  return {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "capitalize",
    backgroundColor: colors.bg,
    color: colors.color,
    border: `1px solid ${colors.border}`,
  };
};

const emptyStyle = {
  padding: "20px",
  borderRadius: "8px",
  border: "1px dashed #c7d2fe",
  backgroundColor: "#f8fafc",
  color: "#64748b",
  fontSize: "14px",
  lineHeight: 1.6,
};

const pendingCalloutStyle = {
  padding: "16px 20px",
  borderRadius: "8px",
  backgroundColor: "#fffbeb",
  border: "1px solid #fcd34d",
  color: "#92400e",
  fontSize: "14px",
  lineHeight: 1.6,
  marginBottom: "16px",
};

const longPendingCalloutStyle = {
  ...pendingCalloutStyle,
  backgroundColor: "#eff6ff",
  border: "1px solid #93c5fd",
  color: "#1e40af",
  marginTop: "12px",
};

const errorStyle = {
  padding: "12px 16px",
  borderRadius: "8px",
  backgroundColor: "#fee2e2",
  border: "1px solid #fca5a5",
  color: "#b91c1c",
  fontSize: "14px",
  marginBottom: "12px",
};

const fieldsGridStyle = {
  display: "grid",
  gap: "16px",
  marginBottom: "16px",
};

const fieldCardStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  backgroundColor: "#fafbff",
  overflow: "hidden",
};

const fieldHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "10px 14px",
  backgroundColor: "#f1f5f9",
  borderBottom: "1px solid #e2e8f0",
};

const fieldLabelStyle = {
  fontSize: "13px",
  fontWeight: 700,
  color: "#3730a3",
  margin: 0,
  textTransform: "capitalize",
};

const fieldCopyButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "4px 10px",
  fontSize: "12px",
  fontWeight: 600,
  color: "#3730a3",
  backgroundColor: "#eef2ff",
  border: "1px solid #c7d2fe",
  borderRadius: "6px",
  cursor: "pointer",
};

const fieldBodyStyle = {
  padding: "14px 16px",
  fontSize: "14px",
  lineHeight: 1.65,
  color: "#1e293b",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: 0,
};

const fieldPlaceholderStyle = {
  ...fieldBodyStyle,
  color: "#94a3b8",
  fontStyle: "italic",
};

const toolbarStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginBottom: "16px",
};

const copyAllButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  height: "36px",
  padding: "0 14px",
  borderRadius: "6px",
  border: "1px solid #3730a3",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "13px",
  cursor: "pointer",
};

const detailsStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  backgroundColor: "#f8fafc",
  overflow: "hidden",
};

const summaryStyle = {
  padding: "12px 16px",
  fontSize: "14px",
  fontWeight: 600,
  color: "#3730a3",
  cursor: "pointer",
  listStyle: "none",
  userSelect: "none",
};

const RESPONSE_FIELDS = [
  { key: "answer", label: "Answer" },
  { key: "summary", label: "Summary" },
  { key: "conclusion", label: "Conclusion" },
  { key: "rawAnswer", label: "Raw answer" },
];

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    return true;
  }
};

const formatFullResponseText = (response) => {
  if (!response) return "";
  return RESPONSE_FIELDS.map(({ key, label }) => {
    const value = response[key];
    const display = value == null || value === "" ? "—" : String(value);
    return `## ${label}\n${display}`;
  }).join("\n\n");
};

const ResponseField = ({ label, value, copyKey, copiedKey, onCopy, isPending }) => {
  const hasValue = value != null && value !== "";
  const displayText = hasValue ? String(value) : null;

  return (
    <div style={fieldCardStyle}>
      <div style={fieldHeaderStyle}>
        <h4 style={fieldLabelStyle}>{label}</h4>
        {hasValue && (
          <button
            type="button"
            style={fieldCopyButtonStyle}
            onClick={() => onCopy(copyKey, displayText)}
            aria-label={`Copy ${label}`}
          >
            {copiedKey === copyKey ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
            {copiedKey === copyKey ? "Copied" : "Copy"}
          </button>
        )}
      </div>
      {hasValue ? (
        <p style={fieldBodyStyle}>{displayText}</p>
      ) : (
        <p style={fieldPlaceholderStyle}>
          {isPending ? "Generation in progress…" : "No content returned."}
        </p>
      )}
    </div>
  );
};

const DashboardAskJobResponse = ({
  jobId,
  response,
  loading,
  error,
  onPoll,
  polling,
}) => {
  const [copiedKey, setCopiedKey] = useState("");
  const [showLongPendingNote, setShowLongPendingNote] = useState(false);

  const responseText = response ? JSON.stringify(response, null, 2) : null;
  const status = String(response?.status || "").toLowerCase();
  const isPending = status === "pending" || (!status && jobId);
  const isFailed = status === "failed";

  useEffect(() => {
    if (!jobId || status !== "pending") {
      setShowLongPendingNote(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      setShowLongPendingNote(true);
    }, LONG_PENDING_MS);

    return () => clearTimeout(timer);
  }, [jobId, status]);

  const handleCopy = useCallback(async (key, text) => {
    if (!text) return;
    await copyToClipboard(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(""), 2000);
  }, []);

  const fullCopyText = useMemo(() => {
    if (!response) return "";
    return formatFullResponseText(response);
  }, [response]);

  return (
    <section style={sectionStyle} aria-labelledby="dashboard-ask-response-heading">
      <div style={headerRowStyle}>
        <h3 id="dashboard-ask-response-heading" style={titleStyle}>
          Job response
        </h3>
        {jobId && onPoll && (
          <button
            type="button"
            style={loading || polling ? pollButtonDisabledStyle : pollButtonStyle}
            onClick={onPoll}
            disabled={loading || polling}
            aria-busy={loading || polling}
          >
            <FiRefreshCw aria-hidden="true" />
            {loading || polling ? "Polling…" : "Poll job"}
          </button>
        )}
      </div>

      {jobId && (
        <p style={metaStyle}>
          Job ID: {jobId}
        </p>
      )}

      {status && (
        <p style={{ marginBottom: "12px" }}>
          <span style={statusBadgeStyle(status)}>{status}</span>
        </p>
      )}

      {error && <div style={errorStyle}>{error}</div>}

      {!jobId && !response && !error && (
        <div style={emptyStyle}>
          Submit a question to start a job. The response will appear here once a job id is returned.
        </div>
      )}

      {jobId && !response && !loading && !error && (
        <div style={emptyStyle}>
          Job created. Use &quot;Poll job&quot; or wait for automatic polling to fetch the result.
        </div>
      )}

      {response && (
        <>
          {isPending && (
            <div style={pendingCalloutStyle} role="status">
              <strong>Generation in progress.</strong> The dashboard agent is processing your question.
              This page polls automatically every few seconds.
              {loading || polling ? " Checking for updates…" : null}
              {showLongPendingNote && (
                <div style={longPendingCalloutStyle}>
                  <strong>Still pending?</strong> If this takes unusually long, ask a backend developer
                  to cross-check that the correct dev branch is deployed.
                </div>
              )}
            </div>
          )}

          {isFailed && (
            <div style={errorStyle}>
              The job failed. Check raw JSON below or contact the backend team if this persists.
            </div>
          )}

          <div style={toolbarStyle}>
            <button
              type="button"
              style={copyAllButtonStyle}
              onClick={() => handleCopy("all-fields", fullCopyText)}
              disabled={!fullCopyText}
            >
              {copiedKey === "all-fields" ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
              {copiedKey === "all-fields" ? "Copied all fields" : "Copy all fields"}
            </button>
            {responseText && (
              <button
                type="button"
                style={pollButtonStyle}
                onClick={() => handleCopy("all-json", responseText)}
              >
                {copiedKey === "all-json" ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
                {copiedKey === "all-json" ? "Copied JSON" : "Copy raw JSON"}
              </button>
            )}
          </div>

          <div style={fieldsGridStyle}>
            {RESPONSE_FIELDS.map(({ key, label }) => (
              <ResponseField
                key={key}
                label={label}
                value={response[key]}
                copyKey={key}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                isPending={isPending}
              />
            ))}
          </div>

          {responseText && (
            <details style={detailsStyle}>
              <summary style={summaryStyle}>View raw JSON response</summary>
              <div style={{ padding: "0 12px 12px" }}>
                <CopyableCodeBlock text={responseText} />
              </div>
            </details>
          )}
        </>
      )}
    </section>
  );
};

export default DashboardAskJobResponse;
